import { resolve, singleton } from 'aurelia';
import { SavedSearch } from '../models';
import { HttpClient } from './http-client';
import { AccountService } from './account-service';
import { CalendarService } from './calendar-service';
import { SubscriptionService } from './subscription-service';

// Owns saved searches: the load menu, the save modal, and persistence. Anonymous visitors
// persist to localStorage; signed-in users persist server-side (with a tier cap) and have
// their local list migrated up on first sign-in. Applies/captures filter state via
// CalendarService and keeps subscription bells consistent via SubscriptionService.
@singleton()
export class SavedSearchService {
  private readonly http: HttpClient = resolve(HttpClient);
  private readonly account: AccountService = resolve(AccountService);
  private readonly calendar: CalendarService = resolve(CalendarService);
  private readonly subscriptions: SubscriptionService = resolve(SubscriptionService);

  static readonly SAVED_SEARCHES_KEY = 'toycalendar.savedSearches';
  savedSearches: SavedSearch[] = [];
  newSearchName: string = '';
  savedSearchMenuOpen: boolean = false;
  saveSearchOpen: boolean = false;
  savedSearchLimit: number = 0;
  savedSearchError: string | null = null;
  // True while saveCurrentSearch is in flight. The save can span several sequential server calls
  // (create, reload, subscription sync), so the modal surfaces this as a busy state rather than
  // leaving the user unsure whether the click registered.
  savingSearch: boolean = false;
  // True while a saved search is being deleted, keyed by the row being removed.
  deletingSearchId: number | null = null;
  // Bound to the "Receive email notifications" checkbox in the save modal. When checked on save,
  // a signed-in user is subscribed to the newly-saved search. Only meaningful when signed in.
  notifyByEmail: boolean = false;

  // Email notifications are subscription-based and require a signed-in account, so the checkbox is
  // only shown/meaningful for authenticated users (matching the saved-search bell visibility).
  get canNotifyByEmail(): boolean {
    return this.account.isAuthenticated;
  }

  // Loads the saved searches list from localStorage, tolerating missing or malformed data.
  // Used for anonymous visitors and as the migration source on first sign-in.
  loadSavedSearches(): void {
    try {
      const raw = localStorage.getItem(SavedSearchService.SAVED_SEARCHES_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;
      this.savedSearches = parsed
        .filter(s => s && typeof s.name === 'string')
        .map(s => ({
          name: s.name,
          searchText: typeof s.searchText === 'string' ? s.searchText : '',
          sources: Array.isArray(s.sources) ? s.sources.filter((x: unknown) => typeof x === 'string') : []
        }));
    } catch {
      // Corrupt storage: start from an empty list rather than breaking the app.
      this.savedSearches = [];
    }
  }

  private persistSavedSearches(): void {
    try {
      localStorage.setItem(SavedSearchService.SAVED_SEARCHES_KEY, JSON.stringify(this.savedSearches));
    } catch {
      // Storage may be full or unavailable (e.g. private mode); ignore silently.
    }
  }

  // Reconciles saved searches with the current auth state. Anonymous visitors keep their
  // localStorage list (already loaded). A signed-in user gets their server list; any
  // locally-saved searches are migrated up on first sign-in, then cleared locally so the
  // server becomes the single source of truth.
  async initSavedSearches(): Promise<void> {
    if (!this.account.isAuthenticated) return;
    const localPending = [...this.savedSearches];
    await this.loadServerSavedSearches();
    if (localPending.length > 0) {
      await this.migrateLocalSearchesToServer(localPending);
    }
  }

  // Fetches the signed-in user's saved searches from the server and captures the tier cap.
  private async loadServerSavedSearches(): Promise<void> {
    try {
      const response = await this.http.get('/api/saved-searches');
      if (!response.ok) return;
      const data = await response.json();
      this.savedSearchLimit = typeof data.limit === 'number' ? data.limit : 0;
      const items = Array.isArray(data.items) ? data.items : [];
      this.savedSearches = items.map((s: any) => ({
        id: s.id,
        name: s.name,
        searchText: typeof s.searchText === 'string' ? s.searchText : '',
        sources: Array.isArray(s.sources) ? s.sources.filter((x: unknown) => typeof x === 'string') : []
      }));
    } catch {
      // Non-fatal: leave whatever list is present.
    }
  }

  // Pushes localStorage searches to the server on first sign-in (stopping at the tier cap),
  // then clears local storage and reloads the authoritative server list.
  private async migrateLocalSearchesToServer(local: SavedSearch[]): Promise<void> {
    const existingNames = new Set(this.savedSearches.map(s => s.name.toLowerCase()));
    let migratedAny = false;
    for (const search of local) {
      if (existingNames.has(search.name.toLowerCase())) continue;
      const response = await this.saveSearchToServer(search);
      if (response === 'limit') break;
      if (response === 'ok') migratedAny = true;
    }
    try {
      localStorage.removeItem(SavedSearchService.SAVED_SEARCHES_KEY);
    } catch {
      // Ignore storage errors.
    }
    if (migratedAny) {
      await this.loadServerSavedSearches();
    }
  }

  // Persists a single search to the server. Returns 'ok', 'limit' (tier cap hit), or 'error'.
  private async saveSearchToServer(search: SavedSearch): Promise<'ok' | 'limit' | 'error'> {
    try {
      const response = await this.http.sendWithCsrf('POST', '/api/saved-searches', {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: search.name,
          searchText: search.searchText,
          sources: search.sources
        })
      });
      if (response.status === 409) return 'limit';
      return response.ok ? 'ok' : 'error';
    } catch {
      return 'error';
    }
  }

  toggleSavedSearchMenu(): void {
    this.savedSearchMenuOpen = !this.savedSearchMenuOpen;
  }

  closeSavedSearchMenu(): void {
    this.savedSearchMenuOpen = false;
  }

  // Opens the "Save search" modal, seeding the name with the current search text so the
  // user can accept it or type something more memorable. The modal component focuses/selects
  // the seeded name itself once it is rendered. When the seeded name matches an existing saved
  // search, the "Receive email notifications" checkbox reflects that search's current
  // subscription state so overwriting it does not silently drop an existing subscription.
  openSaveSearch(): void {
    // Nothing to save without search text; the Save button is also disabled in this state.
    if (!this.calendar.searchText.trim()) return;
    this.savedSearchError = null;
    this.newSearchName = this.calendar.searchText.trim();
    this.notifyByEmail = this.isExistingSearchSubscribed(this.newSearchName);
    this.saveSearchOpen = true;
  }

  // True when a saved search with the given name already exists and currently has an email
  // subscription, so the save modal's checkbox can reflect the persisted state on open.
  private isExistingSearchSubscribed(name: string): boolean {
    const existing = this.savedSearches.find(s => s.name.toLowerCase() === name.toLowerCase());
    return !!existing && this.subscriptions.isSavedSearchSubscribed(existing);
  }

  cancelSaveSearch(): void {
    this.saveSearchOpen = false;
    this.savedSearchError = null;
    this.newSearchName = '';
    this.notifyByEmail = false;
  }

  // Captures the current filter state under a name. Re-saving an existing name overwrites it.
  // Signed-in users persist to the server (subject to the tier cap); anonymous visitors
  // persist to localStorage.
  async saveCurrentSearch(): Promise<void> {
    const name = this.newSearchName.trim();
    if (!name) return;
    if (this.savingSearch) return;
    this.savedSearchError = null;
    const snapshot: SavedSearch = {
      name,
      searchText: this.calendar.searchText,
      sources: this.calendar.activeSourceNames()
    };

    this.savingSearch = true;
    try {
      if (this.account.isAuthenticated) {
        const result = await this.saveSearchToServer(snapshot);
        if (result === 'limit') {
          this.savedSearchError = this.savedSearchLimit > 0
            ? `Free accounts can save up to ${this.savedSearchLimit} searches.`
            : 'Saved-search limit reached.';
          return;
        }
        if (result === 'error') {
          this.savedSearchError = 'Could not save your search. Please try again.';
          return;
        }
        const notify = this.notifyByEmail;
        await this.loadServerSavedSearches();
        // The reloaded list now carries the server row id; apply the checkbox state so it is
        // authoritative — subscribe when checked, unsubscribe when unchecked (e.g. the user
        // cleared it while overwriting an existing search). Best-effort — a failure leaves the
        // bell in its prior state.
        const saved = this.savedSearches.find(s => s.name.toLowerCase() === name.toLowerCase());
        if (saved) {
          await this.subscriptions.setSavedSearchSubscription(saved, notify);
        }
        this.newSearchName = '';
        this.notifyByEmail = false;
        this.saveSearchOpen = false;
        return;
      }

      const existing = this.savedSearches.findIndex(s => s.name.toLowerCase() === name.toLowerCase());
      if (existing >= 0) {
        this.savedSearches.splice(existing, 1, snapshot);
      } else {
        this.savedSearches = [...this.savedSearches, snapshot];
      }
      this.persistSavedSearches();
      this.newSearchName = '';
      this.saveSearchOpen = false;
    } finally {
      this.savingSearch = false;
    }
  }

  // Reapplies a saved search: restores the filter state via CalendarService and closes the menu.
  applySavedSearch(search: SavedSearch): void {
    this.calendar.applyFilterSnapshot(search.searchText, search.sources);
    this.savedSearchMenuOpen = false;
  }

  async deleteSavedSearch(search: SavedSearch): Promise<void> {
    this.savedSearchError = null;
    if (this.account.isAuthenticated && typeof search.id === 'number') {
      if (this.deletingSearchId !== null) return;
      this.deletingSearchId = search.id;
      try {
        const response = await this.http.sendWithCsrf('DELETE', `/api/saved-searches/${search.id}`);
        if (!response.ok && response.status !== 404) {
          this.savedSearchError = 'Could not delete your search. Please try again.';
          return;
        }
      } catch {
        this.savedSearchError = 'Could not delete your search. Please try again.';
        return;
      } finally {
        this.deletingSearchId = null;
      }
      this.savedSearches = this.savedSearches.filter(s => s.id !== search.id);
      if (typeof search.id === 'number') {
        this.subscriptions.forgetSavedSearch(search.id);
      }
      return;
    }
    this.savedSearches = this.savedSearches.filter(s => s !== search);
    this.persistSavedSearches();
  }
}
